/**
 * Build triggers (SPEC decision 31, plan P33): per app, "a push to branch pattern X (optionally touching paths Y) builds
 * profile P for environment E". Hangar registers one repository webhook per repository and verifies every delivery.
 * Pure: the request and response shapes and the path-pattern rules, shared by the API and the console.
 */
import { z } from 'zod';
import { uuid } from './common.js';
import { branchPatternProblem, globMatches } from './glob.js';

/** At most this many path patterns per trigger, each at most `TRIGGER_PATH_MAX_LENGTH` characters. */
export const TRIGGER_PATHS_MAX = 20;
export const TRIGGER_PATH_MAX_LENGTH = 200;
/** At most this many triggers per app. */
export const TRIGGERS_PER_APP_MAX = 20;
/** Pushes to the same branch of an app within this window build only the newest commit. */
export const TRIGGER_DEBOUNCE_MS = 30_000;

/**
 * Why `pattern` is not a path glob, or `undefined`. The same subset as branch patterns (`globMatches`): `*` is the only
 * wildcard and matches any run of characters, `/` included, so `apps/mobile/*` covers everything under that folder. A path
 * is relative to the repository root: no leading `/`, no `..`, no whitespace or control characters, no `?`, `[…]`, `{a,b}`, `!`.
 */
export function pathPatternProblem(pattern: string): string | undefined {
  return PATH_PATTERN_RULES.find(([broken]) => broken(pattern))?.[1];
}

const PATH_PATTERN_RULES: readonly (readonly [broken: (pattern: string) => boolean, message: string])[] = [
  [(p) => p.length === 0, 'must not be empty'],
  [(p) => p.length > TRIGGER_PATH_MAX_LENGTH, `at most ${TRIGGER_PATH_MAX_LENGTH} characters`],
  [(p) => /[\s\p{Cc}]/u.test(p), 'must not contain whitespace or control characters'],
  [(p) => /[?[\]{}!]/.test(p), 'only * is a wildcard: ?, [...], {a,b} and ! are not supported'],
  [(p) => p.startsWith('/') || p.startsWith('./'), 'is relative to the repository root: no leading "/" or "./"'],
  [(p) => p.split('/').includes('..') || p.includes('\\'), 'must not contain ".." or "\\"'],
];

/** The default paths of an app's trigger: its working directory (`apps/mobile/*`), or none (every change) for the repository root. */
export function defaultTriggerPaths(workingDirectory: string): string[] {
  const dir = workingDirectory.trim().replace(/^\.\/+/, '').replace(/\/+$/, '');
  return dir === '' || dir === '.' ? [] : [`${dir}/*`];
}

/** Whether a push that changed `changed` paths concerns a trigger with `patterns` (none = every change). */
export function pathsMatch(changed: readonly string[], patterns: readonly string[]): boolean {
  if (patterns.length === 0) return true;
  return changed.some((path) => patterns.some((p) => globMatches(path, p)));
}

const branchPattern = z.string().superRefine((value, ctx) => {
  const problem = branchPatternProblem(value);
  if (problem) ctx.addIssue({ code: 'custom', message: problem });
});
const pathPattern = z.string().superRefine((value, ctx) => {
  const problem = pathPatternProblem(value);
  if (problem) ctx.addIssue({ code: 'custom', message: problem });
});
/** Path patterns; omitted on create = the app's working directory, `[]` = every change. */
const paths = z.array(pathPattern).max(TRIGGER_PATHS_MAX).refine((list) => new Set(list).size === list.length, 'each path pattern once');

/** `POST /apps/{appId}/build-triggers`. */
export const BuildTriggerCreateRequest = z.object({
  branchPattern,
  paths: paths.optional(),
  profileId: uuid,
  environmentId: uuid,
  enabled: z.boolean().default(true),
}).strict();
export type BuildTriggerCreateRequest = z.infer<typeof BuildTriggerCreateRequest>;

/** `PATCH /build-triggers/{triggerId}` (merge patch). */
export const BuildTriggerPatch = z.object({
  branchPattern: branchPattern.optional(),
  paths: paths.optional(),
  profileId: uuid.optional(),
  environmentId: uuid.optional(),
  enabled: z.boolean().optional(),
}).strict();
export type BuildTriggerPatch = z.infer<typeof BuildTriggerPatch>;

/**
 * The repository webhook Hangar registered for the app's repository:
 *   not_needed        no enabled trigger needs it (nothing registered)
 *   registered        Bitbucket sends pushes to Hangar
 *   needs_permission  the access token cannot manage webhooks (it needs the Webhooks permission)
 *   unreachable       Hangar's address cannot be called from the internet yet (local development without a tunnel)
 *   failed            Bitbucket refused or did not answer; `message` says why
 *   no_repository     the app has no linked repository
 */
export const TriggerWebhookStatus = z.enum(['not_needed', 'registered', 'needs_permission', 'unreachable', 'failed', 'no_repository']);
export type TriggerWebhookStatus = z.infer<typeof TriggerWebhookStatus>;

export const TriggerWebhook = z.object({
  status: TriggerWebhookStatus,
  repository: z.string().nullable(),
  message: z.string().nullable(),
  at: z.string().nullable(),
});
export type TriggerWebhook = z.infer<typeof TriggerWebhook>;

/** What a push did to a trigger: queued (waits 30 s for newer pushes), skipped and why, or ignored. */
export const TriggerDeliveryOutcome = z.enum(['queued', 'skipped_paths', 'skipped_environment', 'superseded']);
export const TriggerRunStatus = z.enum(['pending', 'starting', 'started', 'superseded', 'skipped', 'failed']);
export type TriggerRunStatus = z.infer<typeof TriggerRunStatus>;

export const TriggerLastDelivery = z.object({
  at: z.string(),
  branch: z.string(),
  commitSha: z.string(),
  outcome: TriggerDeliveryOutcome,
});
export const TriggerLastRun = z.object({
  at: z.string(),
  status: TriggerRunStatus,
  branch: z.string(),
  commitSha: z.string(),
  message: z.string().nullable(),
  buildRequestId: z.string().nullable(),
  builds: z.array(z.object({ id: z.string(), number: z.number().int() })),
});
export type TriggerLastRun = z.infer<typeof TriggerLastRun>;

export const BuildTrigger = z.object({
  id: z.string(),
  appId: z.string(),
  projectId: z.string(),
  enabled: z.boolean(),
  branchPattern: z.string(),
  paths: z.array(z.string()),
  profileId: z.string(),
  environmentId: z.string(),
  webhook: TriggerWebhook,
  lastDelivery: TriggerLastDelivery.nullable(),
  lastRun: TriggerLastRun.nullable(),
  createdBy: z.string().nullable(),
  updatedBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type BuildTrigger = z.infer<typeof BuildTrigger>;

export const BuildTriggerList = z.object({ data: z.array(BuildTrigger), webhook: TriggerWebhook });
export type BuildTriggerList = z.infer<typeof BuildTriggerList>;
